import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { AdminInvitationDialog } from "@components/AdminInvitationValidate";
import { InvitationAcceptedDialog } from "@components/InvitationAcceptedDialog";
import { IInvitationDecoded } from "@components/admins/AdminAcceptInvitation";
import { useCancelInvitation } from "@components/invitations/hooks/useCancelInvitation";
import { useGlobalSnackbar } from "src/context/SnackbarContext";

interface AdminInvitationNotificationDialogProps {
  invitationUid?: string;
  notificationId?: string;
  onClose: () => void;
  onMarkAsRead?: (notificationId: string) => void | Promise<void>;
}

export const AdminInvitationNotificationDialog = ({
  invitationUid,
  notificationId,
  onClose,
  onMarkAsRead,
}: AdminInvitationNotificationDialogProps) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useGlobalSnackbar();
  const cancelInvitationMutation = useCancelInvitation();

  const [acceptedCompanyName, setAcceptedCompanyName] = useState("");

  const [showAcceptedDialog, setShowAcceptedDialog] = useState(false);

  const invalidateInvitations = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["received-invitations"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["invitations"],
      }),
    ]);
  };

  const markNotificationAsRead = async () => {
    if (!notificationId) return;

    await onMarkAsRead?.(notificationId);
  };

  const handleAccepted = async (invitation: IInvitationDecoded) => {
    await invalidateInvitations();
    await markNotificationAsRead();

    setAcceptedCompanyName(invitation.companyName);
    setShowAcceptedDialog(true);
  };

  const handleRejected = async () => {
    if (!invitationUid) return;

    try {
      await cancelInvitationMutation.mutateAsync(invitationUid);

      await invalidateInvitations();
      await markNotificationAsRead();

      showSnackbar("Invitation declined.", "success");

      onClose();
    } catch (error) {
      console.error("Could not decline admin invitation:", error);

      showSnackbar("Could not decline invitation.", "error");
    }
  };

  const handleAcceptedDialogClose = () => {
    window.location.reload();
  };

  return (
    <>
      <AdminInvitationDialog
        open={Boolean(invitationUid) && !showAcceptedDialog}
        invitationUid={invitationUid ?? ""}
        onClose={onClose}
        onAccepted={handleAccepted}
        onRejected={handleRejected}
      />

      <InvitationAcceptedDialog open={showAcceptedDialog} companyName={acceptedCompanyName} onClose={handleAcceptedDialogClose} />
    </>
  );
};
